import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';

/**
 * Data passed to the duplicate file dialog
 */
export interface DuplicateFileDialogData {
	filename: string;
	existingFile?: { name: string; size: number; modifiedAt: Date };
	newFile: File;
	isImage: boolean;
	remainingConflicts: number;
}

/**
 * Resolution options for duplicate files
 */
export type DuplicateResolution = 'replace' | 'keep-both' | 'skip';

/**
 * Result returned from the duplicate file dialog
 */
export interface DuplicateFileDialogResult {
	resolution: DuplicateResolution;
	applyToAll: boolean;
}

/**
 * Dialog component for handling duplicate file conflicts during upload.
 * Provides options to Replace, Keep Both (auto-rename), or Skip the file.
 * Includes "Apply to all" checkbox for batch conflict handling.
 */
@Component({
	selector: 'app-duplicate-file-dialog',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		MatDialogModule,
		MatButtonModule,
		MatCheckboxModule,
		MatIconModule
	],
	templateUrl: './duplicate-file-dialog.component.html',
	styleUrls: ['./duplicate-file-dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class DuplicateFileDialogComponent {
	readonly data = inject<DuplicateFileDialogData>(MAT_DIALOG_DATA);
	private readonly dialogRef = inject(MatDialogRef<DuplicateFileDialogComponent>);

	applyToAll = false;

	/**
	 * Close the dialog with the selected resolution
	 */
	resolve(resolution: DuplicateResolution): void {
		const result: DuplicateFileDialogResult = {
			resolution,
			applyToAll: this.applyToAll
		};
		this.dialogRef.close(result);
	}

	/**
	 * Cancel and close the dialog without a resolution
	 */
	cancel(): void {
		this.dialogRef.close(null);
	}

	/**
	 * Format file size in human-readable format
	 */
	formatFileSize(bytes: number | null | undefined): string {
		// Handle edge cases: null, undefined, zero, NaN, negative
		if (bytes === null || bytes === undefined || bytes === 0 || !isFinite(bytes) || bytes < 0) {
			return '0 B';
		}
		const k = 1024;
		const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
		const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
		return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
	}

	/**
	 * Format date for display
	 */
	formatDate(date: Date | string | null | undefined): string {
		if (!date) return 'Unknown';
		const parsed = new Date(date);
		if (isNaN(parsed.getTime())) return 'Invalid date';
		return parsed.toLocaleDateString(undefined, {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit'
		});
	}
}
